import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { reactDnD } from '@yola/ws-ui';
import classNames from 'classnames';
import Trigger from '../../common/components/trigger';
import DragTypes from '../constants/drag-types';
import beginDragHandler from '../helpers/begin-drag-handler';
import endDragHandler from '../helpers/end-drag-handler';
import TriggerIcon from '../../common/components/trigger-icon';
import TriggerLabel from '../../common/components/trigger-label';
import { PANE_TRIGGER_SIZE } from '../../control-pane/constants/sizes';

const { useDrag } = reactDnD;

function DragTriggerContainer({
  elementId,
  label = '',
  width = null,
  glyph = 'move',
  glyphSize = 20,
  glyphStrokeWidth = 1.3,
  isLayerSelectShown = false,
}) {
  const triggerRef = useRef(null);

  const [, drag] = useDrag(
    () => ({
      type: DragTypes.VIEW_ELEMENT,
      item: () => {
        const props = { elementId };
        beginDragHandler(props, null, { triggerRef: triggerRef.current });
        return props;
      },
      end: (item) => {
        endDragHandler(item);
      },
      isDragging: (monitor) => {
        const item = monitor.getItem();
        return item && item.elementId === elementId;
      },
    }),
    [elementId]
  );

  const className = classNames('ws-drag-trigger-wrapper', {
    'ws-drag-trigger-wrapper--labeled': label,
  });
  const triggerIconClass = classNames({
    'ws-drag-trigger__icon-drag': glyph === 'drag',
    'ws-drag-trigger__icon-move': glyph === 'move',
  });
  const triggerLabelClass = classNames({
    'ws-trigger-label--custom-padding': !isLayerSelectShown,
    'ws-trigger-label--custom-with-drag-layer-select-padding': isLayerSelectShown,
  });
  const widthNumber = Number.parseInt(width, 10);
  const widthValue = !Number.isNaN(widthNumber) && `${widthNumber}px`;

  return (
    <div
      className={className}
      ref={(node) => {
        triggerRef.current = node;
        drag(node);
      }}
    >
      <Trigger disableHover width={widthValue || PANE_TRIGGER_SIZE}>
        <TriggerIcon
          className={triggerIconClass}
          glyph={glyph}
          size={glyphSize}
          strokeWidth={glyphStrokeWidth}
        />
        {label && <TriggerLabel className={triggerLabelClass}>{label}</TriggerLabel>}
      </Trigger>
    </div>
  );
}

DragTriggerContainer.propTypes = {
  elementId: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  glyph: PropTypes.string,
  glyphSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  glyphStrokeWidth: PropTypes.number,
  isLayerSelectShown: PropTypes.bool,
};

export default DragTriggerContainer;
